import React, {
  ChangeEvent,
  ReactNode,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
  useRef,
} from 'react';

import { useDrag, useDrop } from 'react-dnd';

import { IPolicy, IPolicyTableColumns, useDeleteRuleMutation } from '@entities/policy';
import ModalConfirm from '@features/modal-confirm';
import { TableActionBtns, ITableActionsConfig } from '@features/table-action-btns';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Chip, IconButton, Stack, Switch, Typography } from '@mui/material';
import { useModal } from '@shared/components/Modal';

import PolicyForm from '../components/PolicyForm';
import PolicyRuleForm from '../components/PolicyRuleForm';
import PolicyRulesTable from '../components/PolicyRulesTable';

interface IProps {
  onDelete: (query: { id: string }) => void;
  onToggle: (query: { id: string; active: boolean }) => void;
  onClone: (id: string) => void;
  onReorder: (ids: string[]) => void;
  policies: IPolicy[];
  onReorderRules: (policyId: string, ids: string[]) => void;
  onLocalReorder: (ids: string[]) => void;
  isReorderLocked?: boolean;
}

export const useTableSettings = ({
  onDelete,
  onToggle,
  onClone,
  onReorder,
  policies,
  onReorderRules,
  onLocalReorder,
  isReorderLocked = false,
}: IProps) => {
  const { openModal, closeModal } = useModal();
  const [deleteRule] = useDeleteRuleMutation();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [rulesLocalOrder, setRulesLocalOrder] = useState<Record<string, string[]>>({});
  const policiesRef = useRef<IPolicy[]>([]);
  policiesRef.current = policies;

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const columnTitles: IPolicyTableColumns = useMemo(
    () => ({
      expand: '',
      priority: 'Priority',
      name: 'Policy Name',
      status: 'On/Off',
      traffic: 'Traffic & Merchants Conditions',
      rules: 'Rules',
      actions: 'Actions',
    }),
    [],
  );

  const renderTraffic = (policy: IPolicy) => {
    if (!policy.conditions?.length) return <Typography variant='body2'>Any traffic</Typography>;

    return (
      <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
        {policy.conditions.map((item, idx) => (
          <Stack
            key={`${policy.id}-${idx}`}
            spacing={0.75}
            sx={{ alignItems: 'flex-start', width: '100%' }}
          >
            {(() => {
              const currencies = Array.isArray(item.currency)
                ? item.currency
                : item.currency
                  ? [item.currency]
                  : [];
              const methods = Array.isArray(item.method)
                ? item.method
                : item.method
                  ? [item.method]
                  : [];
              const traffic = Array.isArray(item.traffic)
                ? item.traffic
                : item.traffic
                  ? [item.traffic]
                  : [];

              return (
                <>
                  <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center'>
                    {currencies.length ? (
                      currencies.map(currency => (
                        <Chip
                          key={currency}
                          sx={{ fontWeight: 600 }}
                          size='small'
                          label={currency}
                        />
                      ))
                    ) : (
                      <Chip sx={{ fontWeight: 600 }} size='small' label='Any currency' />
                    )}
                    {methods.length ? (
                      methods.map(method => (
                        <Chip key={method} size='small' variant='filled' label={method} />
                      ))
                    ) : (
                      <Chip size='small' variant='filled' label='Any method' />
                    )}
                  </Stack>

                  <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center'>
                    {traffic.length ? (
                      traffic.map(itemTraffic => (
                        <Chip key={itemTraffic} size='small' variant='filled' label={itemTraffic} />
                      ))
                    ) : (
                      <Chip size='small' variant='filled' label='Any traffic' />
                    )}
                    <Typography variant='caption' color='text.secondary'>
                      for
                    </Typography>
                    {!item.merchants?.length ? (
                      <Chip size='small' variant='filled' label='Any merchant' />
                    ) : (
                      item.merchants.map(merchant => (
                        <Chip key={merchant} size='small' variant='filled' label={merchant} />
                      ))
                    )}
                  </Stack>
                </>
              );
            })()}

            {idx < policy.conditions.length - 1 && (
              <Box sx={{ width: '100%', height: '1px', bgcolor: 'rgb(229, 231, 235)' }} />
            )}
          </Stack>
        ))}
      </Stack>
    );
  };

  const makeNewPolicyModal = () => <PolicyForm />;

  const makeEditModal = (policy: IPolicy) => <PolicyForm policy={policy} />;

  const makeDeleteModal = (policy: IPolicy) => (
    <ModalConfirm
      title='Delete policy'
      message={`Do you want to delete policy "${policy.name}"?`}
      onConfirm={() => {
        onDelete({ id: policy.id });
        closeModal();
      }}
      onCancel={closeModal}
    />
  );

  const renderItem = (key: keyof IPolicyTableColumns, policy: IPolicy) => {
    switch (key) {
      case 'expand':
        return (
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <DragIndicatorIcon fontSize='small' className='drag-handle' />
            <IconButton size='small' onClick={() => toggleRow(policy.id)} aria-label='toggle'>
              {expandedRows[policy.id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Stack>
        );
      case 'priority':
        return <Chip size='small' variant='filled' label={`${policy.priority}`} />;
      case 'name':
        return (
          <Stack spacing={0.5}>
            <Typography variant='body2' fontWeight={700}>
              {policy.name}
            </Typography>
            {policy.description && (
              <Typography variant='caption' color='text.secondary'>
                {policy.description}
              </Typography>
            )}
            {policy.updated_at && (
              <Typography variant='caption' color='text.secondary'>
                Updated {new Date(policy.updated_at).toLocaleString()}
              </Typography>
            )}
          </Stack>
        );
      case 'status':
        return (
          <Switch
            checked={policy.active}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const nextValue = e.target.checked;
              openModal(
                <ModalConfirm
                  title={`${nextValue ? 'Activate' : 'Deactivate'} policy`}
                  message={`Do you want to ${nextValue ? 'activate' : 'deactivate'} "${policy.name}"?`}
                  onConfirm={() => {
                    onToggle({ id: policy.id, active: nextValue });
                    closeModal();
                  }}
                  onCancel={closeModal}
                />,
                'sm',
              );
            }}
          />
        );
      case 'traffic':
        return renderTraffic(policy);
      case 'rules':
        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <Chip size='small' label={policy['rules-count'] ?? 0} variant='filled' />
          </Stack>
        );
      case 'actions': {
        const config: ITableActionsConfig = {
          clone: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalConfirm
                title='Clone policy'
                message={`Clone "${policy.name}"?`}
                onConfirm={() => {
                  onClone(policy.id);
                  closeModal();
                }}
                onCancel={closeModal}
              />
            ),
            popupSize: 'sm',
          },
          edit: {
            isEnabled: true,
            isVisible: true,
            popupElem: makeEditModal(policy),
            popupSize: 'md',
          },
          delete: {
            isEnabled: true,
            isVisible: true,
            popupElem: makeDeleteModal(policy),
            popupSize: 'sm',
          },
        };

        return <TableActionBtns config={config} />;
      }
      default:
        return null;
    }
  };

  const renderRow = (policy: IPolicy, defaultRow: ReactNode) => {
    if (!isValidElement(defaultRow)) return defaultRow;
    const baseRow = defaultRow as React.ReactElement<unknown>;

    return (
      <PolicyDraggableRow
        key={policy.id}
        policy={policy}
        policies={policiesRef.current}
        onConfirmReorder={onReorder}
        onLocalReorder={onLocalReorder}
        isReorderLocked={isReorderLocked}
        renderRow={baseRow}
        isExpanded={expandedRows[policy.id]}
        columnCount={Object.keys(columnTitles).length}
        renderRules={() => (
          <PolicyRulesTable
            rules={policy.rules ?? []}
            onReorderRules={ids => onReorderRules(policy.id, ids)}
            onOpenRuleForm={() =>
              openModal(<PolicyRuleForm onCancel={closeModal} policyId={policy.id} />, 'md')
            }
            policyId={policy.id}
            localOrderIds={rulesLocalOrder[policy.id] ?? null}
            onLocalReorder={ids => setRulesLocalOrder(prev => ({ ...prev, [policy.id]: ids }))}
            onEditRule={rule =>
              openModal(
                <PolicyRuleForm rule={rule} onCancel={closeModal} policyId={policy.id} />,
                'md',
              )
            }
            onDeleteRule={rule =>
              openModal(
                <ModalConfirm
                  title='Delete rule'
                  message={`Do you want to delete rule "${rule.name}"?`}
                  onConfirm={() => {
                    deleteRule({ id: rule.id });
                    closeModal();
                  }}
                  onCancel={closeModal}
                />,
                'sm',
              )
            }
          />
        )}
      />
    );
  };

  return {
    columnTitles,
    renderItem,
    renderRow,
    makeNewPolicyModal,
  };
};

type DragItem = { id: string; index: number; type: string };

interface IDraggableRowProps {
  policy: IPolicy;
  policies: IPolicy[];
  onConfirmReorder: (ids: string[]) => void;
  onLocalReorder: (ids: string[]) => void;
  isReorderLocked: boolean;
  renderRow: React.ReactElement;
  isExpanded: boolean;
  columnCount: number;
  renderRules: () => React.ReactNode;
}

const PolicyDraggableRow: React.FC<IDraggableRowProps> = ({
  policy,
  policies,
  onConfirmReorder,
  onLocalReorder,
  isReorderLocked,
  renderRow,
  isExpanded,
  columnCount,
  renderRules,
}) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const { openModal, closeModal } = useModal();

  const moveAndConfirm = (
    draggedId: string,
    hoverId: string,
    position: 'top' | 'bottom' | null,
  ) => {
    if (draggedId === hoverId) return;
    const fromIndex = policies.findIndex(p => p.id === draggedId);
    let toIndex = policies.findIndex(p => p.id === hoverId);
    if (fromIndex === -1 || toIndex === -1) return;
    if (position === 'bottom') {
      toIndex += 1;
    }
    if (fromIndex < toIndex) {
      toIndex -= 1;
    }
    const nextOrder = reorderPoliciesLocalByIndex(policies, fromIndex, toIndex);
    const ids = nextOrder.map(p => p.id);
    openModal(
      <ModalConfirm
        title='Change priority'
        message='Do you want to update policy priority?'
        onConfirm={() => {
          onLocalReorder(ids);
          onConfirmReorder(ids);
          closeModal();
        }}
        onCancel={closeModal}
      />,
    );
  };

  const dropPositionRef = useRef<'top' | 'bottom' | null>(null);

  const [{ isOver, draggedId, dropPosition }, drop] = useDrop<
    DragItem,
    void,
    { isOver: boolean; draggedId: string | null; dropPosition: 'top' | 'bottom' | null }
  >({
    accept: 'POLICY_ROW',
    canDrop: () => !isReorderLocked,
    drop: item => moveAndConfirm(item.id, policy.id, dropPositionRef.current),
    hover: (_item, monitor) => {
      if (isReorderLocked) return;
      if (!ref.current) return;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const rect = ref.current.getBoundingClientRect();
      const middleY = (rect.bottom - rect.top) / 2;
      const hoverClientY = clientOffset.y - rect.top;
      dropPositionRef.current = hoverClientY < middleY ? 'top' : 'bottom';
    },
    collect: monitor => {
      const item = monitor.getItem() as DragItem | null;
      if (!monitor.isOver({ shallow: true })) {
        dropPositionRef.current = null;
      }
      return {
        isOver: !isReorderLocked && monitor.isOver({ shallow: true }),
        draggedId: item?.id ?? null,
        dropPosition: dropPositionRef.current,
      };
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'POLICY_ROW',
    item: { id: policy.id, index: policies.findIndex(p => p.id === policy.id) },
    canDrag: () => !isReorderLocked,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const isLastRow = policies[policies.length - 1]?.id === policy.id;
  const [{ isOverEnd }, dropEnd] = useDrop<DragItem, void, { isOverEnd: boolean }>({
    accept: 'POLICY_ROW',
    canDrop: () => !isReorderLocked && isLastRow,
    drop: item => {
      const lastId = policies[policies.length - 1]?.id;
      if (!lastId) return;
      moveAndConfirm(item.id, lastId, 'bottom');
    },
    collect: monitor => ({
      isOverEnd: monitor.isOver({ shallow: true }),
    }),
  });

  const isDropTarget = isOver && draggedId !== policy.id;
  const dropClass =
    isDropTarget && dropPosition
      ? dropPosition === 'top'
        ? 'policy-drop-target-top'
        : 'policy-drop-target-bottom'
      : '';

  return (
    <>
      {cloneElement(renderRow, {
        ref,
        className: `${isExpanded ? 'expanded-row' : ''} ${dropClass}`,
        style: {
          ...(renderRow.props.style || {}),
          opacity: isDragging ? 0.4 : 1,
          cursor: isReorderLocked ? 'not-allowed' : 'grab',
        },
      })}
      {isExpanded && (
        <tr className='policy-rules-row'>
          <td colSpan={columnCount}>{renderRules()}</td>
        </tr>
      )}
      {isLastRow && (
        <tr
          ref={dropEnd}
          className={`policy-drop-zone-row ${isOverEnd ? 'policy-drop-target-top' : ''}`}
        >
          <td colSpan={columnCount} />
        </tr>
      )}
    </>
  );
};

const reorderPoliciesLocalByIndex = (policies: IPolicy[], fromIndex: number, toIndex: number) => {
  const list = [...policies];
  if (fromIndex < 0 || fromIndex >= list.length) return list;
  const [removed] = list.splice(fromIndex, 1);
  const nextIndex = Math.max(0, Math.min(toIndex, list.length));
  list.splice(nextIndex, 0, removed);

  return list.map((p, idx) => ({ ...p, priority: idx + 1 }));
};
